"use client";

import { useState } from "react";
import { MousePointerClick } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { BUTTON_ACTIONS, ButtonBlock } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { normalizeWebUrl } from "../../lib/webUrl";
import { pageName } from "../../lib/pageName";
import {
    PopoverTitle,
    SegmentedToggle,
    chromeBtn,
    chromeBtnOpen,
    popoverShell,
} from "./blockAppearance";

/** What the learner's click does: open a website or go to a page. */
export function ButtonActionPopover({ block }: { block: ButtonBlock }) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const [open, setOpen] = useState(false);
    const action = block.data.action === "page" ? "page" : "url";
    const title = t("lomda-v3-editor.button.action");

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <MousePointerClick className="size-5" />
                    {title}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className={cn(popoverShell, "w-[340px]")}
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>{title}</PopoverTitle>
                    <SegmentedToggle
                        fill
                        label={title}
                        value={action}
                        onChange={(next) =>
                            patchBlockData(block.id, "button", () => ({
                                action: next,
                            }))
                        }
                        options={BUTTON_ACTIONS.map((v) => ({
                            value: v,
                            content: t(`lomda-v3-editor.button.action.${v}`),
                        }))}
                    />
                    {action === "url" ? (
                        <UrlField
                            block={block}
                            onSaved={() => setOpen(false)}
                        />
                    ) : (
                        <PagePicker block={block} />
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function UrlField({
    block,
    onSaved,
}: {
    block: ButtonBlock;
    onSaved: () => void;
}) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const [value, setValue] = useState(block.data.url ?? "");
    const [invalid, setInvalid] = useState(false);
    const label = t("lomda-v3-editor.button.url");

    const save = () => {
        const url = normalizeWebUrl(value);
        if (!url) {
            setInvalid(true);
            return;
        }
        patchBlockData(block.id, "button", (d) =>
            d.url === url ? null : { url }
        );
        onSaved();
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-2">
                <Input
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setInvalid(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && value.trim()) save();
                    }}
                    placeholder={t("lomda-v3-editor.embed.paste")}
                    aria-label={label}
                    aria-invalid={invalid}
                    dir="ltr"
                    className="h-9"
                    autoFocus
                />
                <Button
                    type="button"
                    size="sm"
                    disabled={!value.trim()}
                    onClick={save}
                >
                    {t("common.save")}
                </Button>
            </div>
            {invalid && (
                <p role="alert" className="text-start text-sm text-destructive">
                    {t("lomda-v3-editor.button.url-invalid")}
                </p>
            )}
        </div>
    );
}

function PagePicker({ block }: { block: ButtonBlock }) {
    const { dir } = useLocale();
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const pages = useLomdaV3Editor((s) => s.content.pages);

    return (
        <RadioGroup
            dir={dir}
            value={block.data.pageId ?? ""}
            onValueChange={(pageId) =>
                patchBlockData(block.id, "button", (d) =>
                    d.pageId === pageId ? null : { pageId }
                )
            }
            aria-label={t("lomda-v3-editor.button.choose-page")}
            className="-mx-2 max-h-64 gap-0 overflow-y-auto"
        >
            {pages.map((page, i) => (
                <label
                    key={page.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-foreground transition hover:bg-accent"
                >
                    <RadioGroupItem value={page.id} />
                    <span className="min-w-0 flex-1 truncate text-start">
                        {pageName(page, i)}
                    </span>
                </label>
            ))}
        </RadioGroup>
    );
}
